import { useState, useEffect, useMemo, useCallback } from "react";
import { Tag, HelpCircle, ChevronDown, ChevronRight } from "lucide-react";
import { AppShell } from "../layouts/AppShell";
import { EntityWorkspace } from "../layouts/EntityWorkspace";
import { EntityListSidebar } from "../layouts/EntityListSidebar";
import { EntityHeaderPattern } from "../patterns/EntityHeaderPattern";
import { Button } from "../primitives/Button";
import { Modal } from "../primitives/Modal";
import { Input } from "../primitives/Input";
import { Select } from "../primitives/Select";
import {
  listPromotions,
  listPriceBooks,
  listProducts,
  createPromotion,
  updatePromotion,
  activatePromotion,
  pausePromotion,
  archivePromotion,
  getPromotionUsage,
  type Promotion,
  type PromotionStatus,
  type PromotionType,
  type PromotionScope,
  type StackingPolicy,
  type PriceBook,
  type Product,
} from "../../api/catalog";
import { PromotionsFilters, type PromotionsFiltersState } from "./promotions/PromotionsFilters";
import { PromotionDetailPanel } from "./promotions/PromotionDetailPanel";
import { SimulationPanel } from "./promotions/SimulationPanel";

type AdminPromotionsPageProps = {
  userName?: string;
};

const PROMOTION_TYPE_OPTIONS = [
  { value: "percentage", label: "Pourcentage" },
  { value: "fixed_price", label: "Prix fixe" },
];

const PROMOTION_SCOPE_OPTIONS = [
  { value: "all_products", label: "Tous les produits" },
  { value: "selected_products", label: "Produits sélectionnés" },
];

const STACKING_POLICY_OPTIONS = [
  { value: "best_discount_only", label: "Meilleure promo seulement" },
  { value: "first_match_by_priority", label: "Première par priorité" },
  { value: "stackable_only_if_all_combinable", label: "Empiler si toutes combinables" },
];

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  hardware: "Produits (matériel)",
  package: "Forfaits",
  service_area: "Services (superficie)",
  service_fixed: "Services (prix fixe)",
  subscription: "Abonnements",
  license_credit: "Licences / Crédits",
  custom_service: "Services personnalisés",
  other: "Autres",
};

const defaultFilters: PromotionsFiltersState = {
  status: "",
  period: "today",
  type: "",
  scope: "",
  priceBookIds: [],
  search: "",
};

function ProductTypeGroup({
  label,
  items,
  form,
  setForm,
  setErrors,
  errors,
}: {
  type: string;
  label: string;
  items: Product[];
  form: { product_ids: string[] };
  setForm: (updater: (prev: { product_ids: string[] }) => { product_ids: string[] }) => void;
  setErrors: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  errors: Record<string, string>;
}) {
  const [open, setOpen] = useState(true);
  const ids = items.map((p) => p.id);
  const allSelected = ids.length > 0 && ids.every((id) => form.product_ids.includes(id));
  const someSelected = ids.some((id) => form.product_ids.includes(id));
  const toggleAll = () => {
    if (allSelected) {
      setForm((prev) => ({ ...prev, product_ids: prev.product_ids.filter((id) => !ids.includes(id)) }));
    } else {
      setForm((prev) => ({ ...prev, product_ids: [...new Set([...prev.product_ids, ...ids])] }));
    }
    if (errors.product_ids) setErrors((e) => ({ ...e, product_ids: "" }));
  };
  return (
    <div className="rounded border border-border/50 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 p-2 hover:bg-surface-muted text-left text-sm font-medium text-text"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => el && (el.indeterminate = someSelected && !allSelected)}
          onChange={(e) => { e.stopPropagation(); toggleAll(); }}
          className="h-4 w-4 rounded border-border"
          onClick={(e) => e.stopPropagation()}
        />
        <span>{label}</span>
        <span className="text-xs text-muted">({items.length})</span>
      </button>
      {open && (
        <div className="pl-6 pr-2 pb-2 space-y-0.5">
          {items.map((p) => (
            <label key={p.id} className="flex items-center gap-2 py-1 hover:bg-surface-muted rounded cursor-pointer">
              <input
                type="checkbox"
                checked={form.product_ids.includes(p.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setForm((prev) => ({ ...prev, product_ids: [...prev.product_ids, p.id] }));
                  } else {
                    setForm((prev) => ({ ...prev, product_ids: prev.product_ids.filter((id) => id !== p.id) }));
                  }
                  if (errors.product_ids) setErrors((e2) => ({ ...e2, product_ids: "" }));
                }}
                className="h-4 w-4 rounded border-border"
              />
              <span className="text-sm text-text">{p.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function filterPromotions(
  promotions: Promotion[],
  filters: PromotionsFiltersState
): Promotion[] {
  let list = [...promotions];
  const now = new Date();

  if (filters.status) {
    if (filters.status === "expired") {
      list = list.filter((p) => new Date(p.end_date) < now && p.status === "active");
    } else {
      list = list.filter((p) => p.status === filters.status);
    }
  }

  if (filters.period && filters.period !== "custom") {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endToday = new Date(today);
    endToday.setHours(23, 59, 59, 999);
    if (filters.period === "today") {
      list = list.filter((p) => {
        const start = new Date(p.start_date);
        const end = new Date(p.end_date);
        return start <= endToday && end >= today;
      });
    } else if (filters.period === "7days") {
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - 7);
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() + 7);
      list = list.filter((p) => {
        const start = new Date(p.start_date);
        const end = new Date(p.end_date);
        return start <= weekEnd && end >= weekStart;
      });
    } else if (filters.period === "month") {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
      list = list.filter((p) => {
        const start = new Date(p.start_date);
        const end = new Date(p.end_date);
        return start <= monthEnd && end >= monthStart;
      });
    }
  }

  if (filters.type) {
    list = list.filter((p) => p.promotion_type === filters.type);
  }
  if (filters.scope) {
    list = list.filter((p) => p.scope === filters.scope);
  }
  if (filters.priceBookIds.length > 0) {
    list = list.filter((p) =>
      (p.target_price_book_ids || []).some((id) => filters.priceBookIds.includes(id))
    );
  }
  if (filters.search.trim()) {
    const term = filters.search.trim().toLowerCase();
    list = list.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        (p.code || "").toLowerCase().includes(term)
    );
  }
  return list;
}

export function AdminPromotionsPage({ userName = "Utilisateur" }: AdminPromotionsPageProps) {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [priceBooks, setPriceBooks] = useState<PriceBook[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [filters, setFilters] = useState<PromotionsFiltersState>(defaultFilters);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState("details");
  const [simulationOpen, setSimulationOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEditing, setModalEditing] = useState(false);
  const [usageSummary, setUsageSummary] = useState<Record<string, { count: number; totalDiscount: number }>>({});
  const [form, setForm] = useState({
    name: "",
    description: "",
    applicationMode: "auto" as "auto" | "code",
    code: "",
    promotion_type: "percentage" as PromotionType,
    value: "",
    scope: "all_products" as PromotionScope,
    product_ids: [] as string[],
    target_price_book_ids: [] as string[],
    start_date: "",
    end_date: "",
    end_date_days_offset: "" as string,
    end_source: "date" as "date" | "days",
    is_combinable: false,
    priority: "100",
    stacking_policy: "best_discount_only" as StackingPolicy,
    respect_floor_price: true,
    min_purchase_amount: "",
    max_uses: "",
    max_uses_per_user: "",
    excluded_product_ids: [] as string[],
    status: "draft" as PromotionStatus,
    requires_code: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);

  const filteredPromotions = useMemo(
    () => filterPromotions(promotions, filters),
    [promotions, filters]
  );
  const priceBooksTarget = useMemo(
    () => priceBooks.filter((pb) => pb.name !== "promo_xlflow_launch_2026" && !String(pb.name || "").toLowerCase().includes("promo_xlflow_launch")),
    [priceBooks]
  );
  const productsByType = useMemo(() => {
    const map = new Map<string, Product[]>();
    products.forEach((p) => {
      const t = p.product_type || "other";
      if (!map.has(t)) map.set(t, []);
      map.get(t)!.push(p);
    });
    const order = ["package", "service_area", "service_fixed", "custom_service", "subscription", "license_credit", "hardware", "other"];
    return order.filter((k) => map.has(k)).map((k) => ({ type: k, items: map.get(k)! }));
  }, [products]);
  const selectedPromotion = useMemo(
    () => promotions.find((p) => p.id === selectedId) ?? null,
    [promotions, selectedId]
  );

  const getPromoStatusLabel = useCallback((promo: Promotion): { label: string; tone: "success" | "warning" | "danger" | "neutral" } => {
    const now = new Date();
    const end = new Date(promo.end_date);
    if (promo.status === "archived") return { label: "Archivée", tone: "neutral" };
    if (promo.status === "draft") return { label: "Brouillon", tone: "neutral" };
    if (promo.status === "paused") return { label: "Pausée", tone: "warning" };
    if (end < now) return { label: "Expirée", tone: "danger" };
    if (new Date(promo.start_date) > now) return { label: "À venir", tone: "neutral" };
    return { label: "Active", tone: "success" };
  }, []);

  const listItems = useMemo(
    () =>
      filteredPromotions.map((promo) => {
        const typeLabel = promo.promotion_type === "percentage" ? `${promo.value}%` : `${promo.value} $`;
        const period = `${new Date(promo.start_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} → ${new Date(promo.end_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`;
        return {
          id: promo.id,
          title: promo.name,
          subtitle: promo.code ? `Code: ${promo.code}` : period,
          info: typeLabel,
          icon: <Tag size={16} strokeWidth={1.5} />,
          status: getPromoStatusLabel(promo),
        };
      }),
    [filteredPromotions, getPromoStatusLabel]
  );

  useEffect(() => {
    let mounted = true;
    Promise.all([
      listPromotions().then((data) => {
        if (!mounted) return;
        setPromotions(data);
        if (data.length > 0 && !selectedId) setSelectedId(data[0].id);
      }),
      listPriceBooks().then((data) => {
        if (!mounted) return;
        setPriceBooks(data);
      }),
      listProducts({ page: 1, page_size: 1000 }).then((data) => {
        if (!mounted) return;
        setProducts(data.items || []);
      }),
    ])
      .catch(console.error)
      .finally(() => {
        if (mounted) setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    getPromotionUsage(selectedId)
      .then((usage) => {
        const totalDiscount = usage.reduce((s, u) => s + u.discount_amount, 0);
        setUsageSummary((prev) => ({
          ...prev,
          [selectedId]: { count: usage.length, totalDiscount },
        }));
      })
      .catch(() => {});
  }, [selectedId]);

  const refreshPromotions = useCallback(async () => {
    const data = await listPromotions();
    setPromotions(data);
  }, []);

  const handleCreate = () => {
    setModalEditing(false);
    setForm({
      name: "",
      description: "",
      applicationMode: "auto",
      code: "",
      promotion_type: "percentage",
      value: "",
      scope: "all_products",
      product_ids: [],
      target_price_book_ids: [],
      start_date: "",
      end_date: "",
      end_date_days_offset: "",
      end_source: "date",
      is_combinable: false,
      priority: "100",
      stacking_policy: "best_discount_only",
      respect_floor_price: true,
      min_purchase_amount: "",
      max_uses: "",
      max_uses_per_user: "",
      excluded_product_ids: [],
      status: "draft",
      requires_code: false,
    });
    setErrors({});
    setModalOpen(true);
  };

  const handleEdit = useCallback(() => {
    if (!selectedPromotion) return;
    setModalEditing(true);
    const start = selectedPromotion.start_date.replace(/\.\d{3}Z$/, "Z");
    const end = selectedPromotion.end_date.replace(/\.\d{3}Z$/, "Z");
    setForm({
      name: selectedPromotion.name,
      description: selectedPromotion.description || "",
      applicationMode: selectedPromotion.code ? "code" : "auto",
      code: selectedPromotion.code || "",
      promotion_type: selectedPromotion.promotion_type === "fixed_amount" ? "fixed_price" : selectedPromotion.promotion_type,
      value: selectedPromotion.value.toString(),
      scope: selectedPromotion.scope === "product_categories" ? "all_products" : selectedPromotion.scope,
      product_ids: selectedPromotion.product_ids || [],
      target_price_book_ids: selectedPromotion.target_price_book_ids || [],
      start_date: start.slice(0, 16),
      end_date: end.slice(0, 16),
      end_date_days_offset: "",
      end_source: "date" as "date" | "days",
      is_combinable: selectedPromotion.is_combinable,
      priority: selectedPromotion.priority.toString(),
      stacking_policy: selectedPromotion.stacking_policy,
      respect_floor_price: selectedPromotion.respect_floor_price,
      min_purchase_amount: selectedPromotion.min_purchase_amount?.toString() || "",
      max_uses: selectedPromotion.max_uses?.toString() || "",
      max_uses_per_user: selectedPromotion.max_uses_per_user?.toString() || "",
      excluded_product_ids: selectedPromotion.excluded_product_ids || [],
      status: selectedPromotion.status,
      requires_code: !!selectedPromotion.code,
    });
    setErrors({});
    setModalOpen(true);
  }, [selectedPromotion]);

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = "Le nom est requis";
    if (form.requires_code && !form.code?.trim()) newErrors.code = "Le code promo est requis lorsque l'option est cochée";
    if (!form.value || parseFloat(form.value) <= 0) newErrors.value = "La valeur doit être supérieure à 0";
    if (form.promotion_type === "percentage" && parseFloat(form.value) > 100)
      newErrors.value = "Le pourcentage ne peut pas dépasser 100%";
    if (!form.start_date) newErrors.start_date = "La date de début est requise";
    let endDate = form.end_date;
    if (form.end_date_days_offset?.trim()) {
      const days = parseInt(form.end_date_days_offset, 10);
      if (Number.isNaN(days) || days < 1) newErrors.end_date_days_offset = "Nombre de jours invalide";
      else {
        const start = new Date(form.start_date);
        const end = new Date(start);
        end.setDate(end.getDate() + days);
        endDate = end.toISOString().slice(0, 16);
      }
    }
    if (!endDate) newErrors.end_date = "La date de fin est requise (ou indiquez un nombre de jours)";
    if (form.start_date && endDate && new Date(form.start_date) >= new Date(endDate))
      newErrors.end_date = "La date de fin doit être après la date de début";
    if (form.scope === "selected_products" && form.product_ids.length === 0)
      newErrors.product_ids = "Au moins un produit doit être sélectionné";
    if (form.target_price_book_ids.length === 0)
      newErrors.target_price_book_ids = "Au moins un pricebook doit être sélectionné";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const finalEndDate = endDate ? new Date(endDate).toISOString() : new Date(form.end_date).toISOString();
    const codeToSend = form.requires_code ? (form.code?.toUpperCase().trim() || null) : null;

    try {
      const payload = {
        name: form.name,
        description: form.description || null,
        code: codeToSend,
        promotion_type: form.promotion_type,
        value: parseFloat(form.value),
        scope: form.scope,
        product_ids: form.scope === "selected_products" ? form.product_ids : null,
        product_category_filters: null,
        target_price_book_ids: form.target_price_book_ids,
        start_date: new Date(form.start_date).toISOString(),
        end_date: finalEndDate,
        is_combinable: form.is_combinable,
        priority: parseInt(form.priority) || 100,
        stacking_policy: form.stacking_policy,
        respect_floor_price: form.respect_floor_price,
        min_purchase_amount: form.min_purchase_amount ? parseFloat(form.min_purchase_amount) : null,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        max_uses_per_user: form.max_uses_per_user ? parseInt(form.max_uses_per_user) : null,
        excluded_product_ids: form.excluded_product_ids.length > 0 ? form.excluded_product_ids : null,
        status: form.status,
      };
      if (modalEditing && selectedId) {
        await updatePromotion(selectedId, payload);
      } else {
        await createPromotion(payload);
      }
      await refreshPromotions();
      setModalOpen(false);
    } catch (error) {
      alert(`Erreur: ${(error as Error).message || "Veuillez réessayer."}`);
    }
  };

  const handleActivate = useCallback(async () => {
    if (!selectedId) return;
    await activatePromotion(selectedId);
    await refreshPromotions();
  }, [selectedId, refreshPromotions]);

  const handlePause = useCallback(async () => {
    if (!selectedId) return;
    await pausePromotion(selectedId);
    await refreshPromotions();
  }, [selectedId, refreshPromotions]);

  const handleArchive = useCallback(async () => {
    if (!selectedId) return;
    if (!confirm("Archiver cette promotion ?")) return;
    await archivePromotion(selectedId);
    await refreshPromotions();
    setSelectedId(null);
  }, [selectedId, refreshPromotions]);

  const handleDuplicate = useCallback(
    async (id: string) => {
      const promo = promotions.find((p) => p.id === id);
      if (!promo) return;
      try {
        const payload = {
          name: `Copie de ${promo.name}`,
          description: promo.description || null,
          code: promo.code ? `${promo.code}-COPY` : null,
          promotion_type: promo.promotion_type,
          value: promo.value,
          scope: promo.scope,
          product_ids: promo.product_ids || null,
          product_category_filters: promo.product_category_filters || null,
          target_price_book_ids: promo.target_price_book_ids,
          start_date: promo.start_date,
          end_date: promo.end_date,
          is_combinable: promo.is_combinable,
          priority: promo.priority,
          stacking_policy: promo.stacking_policy,
          respect_floor_price: promo.respect_floor_price,
          min_purchase_amount: promo.min_purchase_amount,
          max_uses: promo.max_uses,
          max_uses_per_user: promo.max_uses_per_user,
          excluded_product_ids: promo.excluded_product_ids,
          status: "draft" as PromotionStatus,
        };
        const created = await createPromotion(payload);
        await refreshPromotions();
        setSelectedId(created.id);
      } catch (error) {
        alert(`Erreur: ${(error as Error).message || "Veuillez réessayer."}`);
      }
    },
    [promotions, refreshPromotions]
  );

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted">Chargement...</p>
      </div>
    );
  }

  return (
    <AppShell
      topbar={<div className="text-sm text-muted">Admin • Promotions</div>}
      userName={userName}
      modules={[
        { label: "Tableau de bord", onClick: () => (window.location.hash = "#/") },
        { label: "Catalogue", onClick: () => (window.location.hash = "#/admin-products") },
        { label: "Promotions", onClick: () => (window.location.hash = "#/admin-promotions") },
        { label: "Inventaire", onClick: () => (window.location.hash = "#/inventories") },
      ]}
    >
      <EntityWorkspace
        header={
          <>
            {selectedPromotion ? (
              <EntityHeaderPattern
                title={selectedPromotion.name}
                subtitle={selectedPromotion.description || undefined}
                metaItems={[
                  { label: "Type", value: selectedPromotion.promotion_type === "percentage" ? `${selectedPromotion.value}%` : `${selectedPromotion.value} $`, tone: "default" },
                  {
                    label: "Période",
                    value: `${new Date(selectedPromotion.start_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })} → ${new Date(selectedPromotion.end_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`,
                    tone: "muted",
                  },
                ]}
                status={{ label: getPromoStatusLabel(selectedPromotion).label, tone: getPromoStatusLabel(selectedPromotion).tone }}
                primaryAction={{ label: "Créer une promotion", onClick: handleCreate }}
                secondaryActions={[
                  { label: "Simuler", onClick: () => setSimulationOpen(true) },
                  { label: "Modifier", onClick: handleEdit },
                  ...(selectedPromotion.status === "draft" || selectedPromotion.status === "paused"
                    ? [{ label: "Activer", onClick: () => handleActivate() }]
                    : selectedPromotion.status === "active"
                      ? [{ label: "Pause", onClick: () => handlePause() }]
                      : []),
                  ...(selectedPromotion.status !== "archived" ? [{ label: "Archiver", onClick: () => handleArchive() }] : []),
                ].filter(Boolean) as { label: string; onClick?: () => void }[]}
              />
            ) : (
              <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-title text-text font-semibold">Promotions</h2>
                    <p className="text-sm text-muted mt-1">Sélectionnez une promotion ou créez-en une nouvelle.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button label="Simuler" intent="default" size="sm" onClick={() => setSimulationOpen(true)} />
                    <Button label="Créer une promotion" intent="primary" size="sm" onClick={handleCreate} />
                  </div>
                </div>
              </div>
            )}
            <PromotionsFilters
              filters={filters}
              onFiltersChange={setFilters}
              priceBooks={priceBooksTarget}
            />
          </>
        }
        sidebarTitle="LISTE"
        sidebarIcon="🏷️"
        sidebarMeta={filteredPromotions.length ? `${filteredPromotions.length}` : undefined}
        sidebarItems={[]}
        allowHoverOpen
        renderSidebar={(isExpanded) => (
          <EntityListSidebar
            sections={[]}
            items={listItems}
            selectedId={selectedId}
            onSelect={setSelectedId}
            isExpanded={isExpanded}
            searchPlaceholder="Rechercher une promotion"
            filterOptions={["Tous les statuts", "Brouillon", "Active", "Pausée", "Expirée", "Archivée"]}
            emptyMessage="Aucune entité disponible."
            addActionLabel="+ Ajouter"
            onAddAction={handleCreate}
          />
        )}
        content={
          selectedId && selectedPromotion ? (
            <PromotionDetailPanel
              promotion={selectedPromotion}
              priceBooks={priceBooks}
              products={products}
              activeTab={detailTab}
              onTabChange={setDetailTab}
              onActivate={handleActivate}
              onPause={handlePause}
              onArchive={handleArchive}
              onEdit={handleEdit}
              isLocked={selectedPromotion.status === "active"}
            />
          ) : (
            <div className="rounded-md border border-border bg-surface p-6 shadow-sm text-sm text-muted">
              Sélectionnez une promotion pour voir les détails.
            </div>
          )
        }
      />

      {simulationOpen && (
        <SimulationPanel
          onClose={() => setSimulationOpen(false)}
          priceBooks={priceBooks}
          products={products}
        />
      )}

      <Modal
        title={modalEditing ? "Modifier la promotion" : "Créer une promotion"}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        showDefaultButtons={false}
        showCloseButton
        size="2xl"
      >
        <div className="space-y-4 max-h-[80vh] overflow-y-auto">
          <Input
            label="Nom *"
            value={form.name}
            onChange={(e) => {
              setForm({ ...form, name: e.target.value });
              if (errors.name) setErrors({ ...errors, name: "" });
            }}
            state={errors.name ? "error" : "default"}
          />
          {errors.name && <p className="mt-1 text-xs text-danger">{errors.name}</p>}

          <div>
            <label className="block text-sm font-medium text-text mb-2">Description</label>
            <textarea
              className="w-full h-20 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Select
              label="Type *"
              options={PROMOTION_TYPE_OPTIONS.map((o) => o.label)}
              value={PROMOTION_TYPE_OPTIONS.find((o) => o.value === form.promotion_type)?.label ?? ""}
              onChange={(label) => {
                const opt = PROMOTION_TYPE_OPTIONS.find((o) => o.label === label);
                if (opt) setForm({ ...form, promotion_type: opt.value as PromotionType });
              }}
            />
            <div>
              <label className="block text-sm font-medium text-text mb-2">
                {form.promotion_type === "percentage" ? "Pourcentage (%) *" : "Valeur (CAD) *"}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max={form.promotion_type === "percentage" ? 100 : undefined}
                className="w-full h-10 rounded-md border border-border bg-surface px-3 text-text"
                value={form.value}
                onChange={(e) => {
                  setForm({ ...form, value: e.target.value });
                  if (errors.value) setErrors({ ...errors, value: "" });
                }}
              />
              {errors.value && <p className="mt-1 text-xs text-danger">{errors.value}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-2">PriceBooks ciblés *</label>
            <p className="text-xs text-muted mb-2">
              La promotion s'applique seulement aux prix issus de ces pricebooks (public, ambassadeur).
            </p>
            <div className="max-h-40 overflow-y-auto border border-border rounded-md p-2 space-y-1">
              {priceBooksTarget.map((pb) => (
                <label key={pb.id} className="flex items-center gap-2 p-1 hover:bg-surface-muted rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.target_price_book_ids.includes(pb.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setForm({ ...form, target_price_book_ids: [...form.target_price_book_ids, pb.id] });
                      } else {
                        setForm({
                          ...form,
                          target_price_book_ids: form.target_price_book_ids.filter((id) => id !== pb.id),
                        });
                      }
                      if (errors.target_price_book_ids) setErrors({ ...errors, target_price_book_ids: "" });
                    }}
                    className="h-4 w-4 rounded border-border"
                  />
                  <span className="text-sm text-text">{pb.name} ({pb.scope})</span>
                </label>
              ))}
            </div>
            {errors.target_price_book_ids && (
              <p className="mt-1 text-xs text-danger">{errors.target_price_book_ids}</p>
            )}
          </div>

          <div>
            <Select
              label="Portée *"
              options={PROMOTION_SCOPE_OPTIONS.map((o) => o.label)}
              value={PROMOTION_SCOPE_OPTIONS.find((o) => o.value === form.scope)?.label ?? ""}
              onChange={(label) => {
                const opt = PROMOTION_SCOPE_OPTIONS.find((o) => o.label === label);
                if (opt) setForm({ ...form, scope: opt.value as PromotionScope, product_ids: [] });
              }}
            />
          </div>

          {form.scope === "selected_products" && (
            <div>
              <label className="block text-sm font-medium text-text mb-2">Produits inclus *</label>
              <p className="text-xs text-muted mb-1">Sélectionnez une catégorie (tous les produits) ou des produits individuellement.</p>
              <div className="max-h-48 overflow-y-auto border border-border rounded-md p-2 space-y-1">
                {productsByType.map(({ type, items }) => (
                  <ProductTypeGroup
                    key={type}
                    type={type}
                    label={PRODUCT_TYPE_LABELS[type] || type}
                    items={items}
                    form={form}
                    setForm={(updater) => setForm((prev) => ({ ...prev, ...updater(prev) }))}
                    setErrors={setErrors}
                    errors={errors}
                  />
                ))}
              </div>
              {errors.product_ids && <p className="mt-1 text-xs text-danger">{errors.product_ids}</p>}
            </div>
          )}

          {form.scope === "all_products" && (
            <div>
              <label className="block text-sm font-medium text-text mb-2">Produits exclus (optionnel)</label>
              <p className="text-xs text-muted mb-1">Exclure certains produits de la promotion.</p>
              <div className="max-h-32 overflow-y-auto border border-border rounded-md p-2 space-y-1">
                {products.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 p-1 hover:bg-surface-muted rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.excluded_product_ids.includes(p.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setForm({ ...form, excluded_product_ids: [...form.excluded_product_ids, p.id] });
                        } else {
                          setForm({
                            ...form,
                            excluded_product_ids: form.excluded_product_ids.filter((id) => id !== p.id),
                          });
                        }
                      }}
                      className="h-4 w-4 rounded border-border"
                    />
                    <span className="text-sm text-text">{p.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-text mb-2">Début *</label>
              <input
                type="datetime-local"
                className="w-full h-10 rounded-md border border-border bg-surface px-3 text-text"
                value={form.start_date}
                onChange={(e) => {
                  const start = e.target.value;
                  setForm((prev) => {
                    const next = { ...prev, start_date: start };
                    if (prev.end_source === "days" && prev.end_date_days_offset?.trim()) {
                      const days = parseInt(prev.end_date_days_offset, 10);
                      if (!Number.isNaN(days)) {
                        const end = new Date(start);
                        end.setDate(end.getDate() + days);
                        next.end_date = end.toISOString().slice(0, 16);
                      }
                    }
                    return next;
                  });
                  if (errors.start_date) setErrors({ ...errors, start_date: "" });
                }}
              />
              {errors.start_date && <p className="mt-1 text-xs text-danger">{errors.start_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-2">Fin *</label>
              {form.end_source === "date" ? (
                <input
                  type="datetime-local"
                  className="w-full h-10 rounded-md border border-border bg-surface px-3 text-text"
                  value={form.end_date}
                  onChange={(e) => {
                    setForm({ ...form, end_date: e.target.value, end_source: "date" });
                    if (errors.end_date) setErrors({ ...errors, end_date: "" });
                  }}
                />
              ) : (
                <div className="h-10 rounded-md border border-border bg-surface-muted/50 px-3 flex items-center text-sm text-muted">
                  {form.start_date && form.end_date_days_offset?.trim()
                    ? (() => {
                        const d = new Date(form.start_date);
                        d.setDate(d.getDate() + parseInt(form.end_date_days_offset, 10));
                        return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
                      })()
                    : "—"}
                </div>
              )}
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted">ou</span>
                {form.end_source === "days" ? (
                  <input
                    type="number"
                    min={1}
                    placeholder="Nb jours après le début"
                    className="h-8 w-36 rounded-md border border-border bg-surface px-2 text-sm text-text"
                    value={form.end_date_days_offset}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm((prev) => {
                        const next = { ...prev, end_date_days_offset: val, end_source: "days" as const };
                        if (prev.start_date && val.trim()) {
                          const days = parseInt(val, 10);
                          if (!Number.isNaN(days)) {
                            const end = new Date(prev.start_date);
                            end.setDate(end.getDate() + days);
                            next.end_date = end.toISOString().slice(0, 16);
                          }
                        }
                        return next;
                      });
                      if (errors.end_date_days_offset) setErrors({ ...errors, end_date_days_offset: "" });
                    }}
                  />
                ) : (
                  <span className="text-sm text-muted">
                    {form.start_date && form.end_date
                      ? `${Math.round((new Date(form.end_date).getTime() - new Date(form.start_date).getTime()) / (24 * 60 * 60 * 1000))} jours après le début`
                      : "—"}
                  </span>
                )}
                <span className="text-xs text-muted">jours après le début</span>
                <button
                  type="button"
                  onClick={() => {
                    if (form.end_source === "date") {
                      const days =
                        form.start_date && form.end_date
                          ? Math.round((new Date(form.end_date).getTime() - new Date(form.start_date).getTime()) / (24 * 60 * 60 * 1000))
                          : "";
                      setForm({ ...form, end_source: "days", end_date_days_offset: String(days || "") });
                    } else {
                      setForm({ ...form, end_source: "date" });
                    }
                  }}
                  className="text-xs text-primary hover:underline"
                >
                  {form.end_source === "date" ? "Saisir par nombre de jours" : "Saisir par date de fin"}
                </button>
              </div>
              {errors.end_date && <p className="mt-1 text-xs text-danger">{errors.end_date}</p>}
              {errors.end_date_days_offset && <p className="mt-1 text-xs text-danger">{errors.end_date_days_offset}</p>}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-sm font-medium text-text">Politique d&apos;empilement</label>
                <span className="group relative">
                  <HelpCircle size={14} className="text-muted cursor-help" />
                  <span className="absolute right-0 bottom-full mb-1 hidden group-hover:block w-64 p-2 text-xs rounded bg-surface-muted border border-border text-text shadow z-10">
                    Détermine comment plusieurs promotions sont appliquées : meilleure seule, première par priorité, ou empilables si toutes sont combinables.
                  </span>
                </span>
              </div>
              <Select
                label=""
                options={STACKING_POLICY_OPTIONS.map((o) => o.label)}
                value={STACKING_POLICY_OPTIONS.find((o) => o.value === form.stacking_policy)?.label ?? ""}
                onChange={(label) => {
                  const opt = STACKING_POLICY_OPTIONS.find((o) => o.label === label);
                  if (opt) setForm({ ...form, stacking_policy: opt.value as StackingPolicy });
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-2">Priorité</label>
              <input
                type="number"
                className="w-full h-10 rounded-md border border-border bg-surface px-3 text-text"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_combinable}
                onChange={(e) => setForm({ ...form, is_combinable: e.target.checked })}
                className="h-4 w-4 rounded border-border"
              />
              <span className="text-sm text-text">Combinable</span>
              <span className="group relative">
                <HelpCircle size={14} className="text-muted cursor-help" />
                <span className="absolute left-0 bottom-full mb-1 hidden group-hover:block w-56 p-2 text-xs rounded bg-surface-muted border border-border text-text shadow z-10">
                  Si coché, cette promotion peut être appliquée avec d&apos;autres promotions sur le même panier (selon la politique d&apos;empilement).
                </span>
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.respect_floor_price}
                onChange={(e) => setForm({ ...form, respect_floor_price: e.target.checked })}
                className="h-4 w-4 rounded border-border"
              />
              <span className="text-sm text-text">Respecter le prix plancher</span>
              <span className="group relative">
                <HelpCircle size={14} className="text-muted cursor-help" />
                <span className="absolute left-0 bottom-full mb-1 hidden group-hover:block w-56 p-2 text-xs rounded bg-surface-muted border border-border text-text shadow z-10">
                  Si coché, le prix après promotion ne descendra pas en dessous du prix plancher défini pour le produit.
                </span>
              </span>
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-text mb-2">Min. achat (CAD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full h-10 rounded-md border border-border bg-surface px-3 text-text"
                value={form.min_purchase_amount}
                onChange={(e) => setForm({ ...form, min_purchase_amount: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-2">Limite globale</label>
              <input
                type="number"
                min="0"
                className="w-full h-10 rounded-md border border-border bg-surface px-3 text-text"
                value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-2">Limite / utilisateur</label>
              <input
                type="number"
                min="0"
                className="w-full h-10 rounded-md border border-border bg-surface px-3 text-text"
                value={form.max_uses_per_user}
                onChange={(e) => setForm({ ...form, max_uses_per_user: e.target.value })}
              />
            </div>
          </div>

          <div className="border-t border-border pt-3 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.requires_code}
                onChange={(e) => {
                setForm({ ...form, requires_code: e.target.checked });
                if (errors.code) setErrors({ ...errors, code: "" });
              }}
                className="h-4 w-4 rounded border-border"
              />
              <span className="text-sm text-text">Code Promo</span>
            </label>
            <p className="text-xs text-muted">Si coché, la promotion s&apos;applique selon les mêmes critères mais uniquement lorsque le client saisit le code ci-dessous.</p>
            {form.requires_code && (
              <div>
                <label className="block text-sm font-medium text-text mb-1">Code *</label>
                <input
                  type="text"
                  placeholder="ex. PROMO2026"
                  className="w-full max-w-xs h-10 rounded-md border border-border bg-surface px-3 text-text uppercase"
                  value={form.code}
                  onChange={(e) => {
                    setForm({ ...form, code: e.target.value.toUpperCase() });
                    if (errors.code) setErrors({ ...errors, code: "" });
                  }}
                />
                {errors.code && <p className="mt-1 text-xs text-danger">{errors.code}</p>}
              </div>
            )}
          </div>

          {!modalEditing && (
            <div>
              <Select
                label="Statut initial"
                options={["Brouillon", "Active"]}
                value={form.status === "draft" ? "Brouillon" : "Active"}
                onChange={(label) =>
                  setForm({ ...form, status: label === "Active" ? "active" : "draft" })
                }
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button label="Annuler" intent="default" size="sm" onClick={() => setModalOpen(false)} />
            <Button label={modalEditing ? "Enregistrer" : "Créer"} intent="primary" size="sm" onClick={handleSave} />
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}
